// File: src/context/UserContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";

import { useAuth } from "@/context/AuthProvider.jsx";

/* ──────────────────────────────────────────────────────────────
 * Safe unauthenticated shape
 *
 * Keep a stable object shape for legacy consumers without
 * manufacturing any privilege.
 * ────────────────────────────────────────────────────────────── */

const EMPTY_USER = Object.freeze({
  id: null,
  staff_id: null,
  company_id: null,
  unit_id: null,
  unit_slug: null,

  full_name: null,
  email: null,

  role: null,
  department: null,

  is_developer: false,
  is_platform_admin: false,
  isAuthenticated: false,

  roles: [],
  permissions: [],
  cost_centres: [],
  allowedCostCentres: [],
  modules: [],
});

/* ──────────────────────────────────────────────────────────────
 * Context
 *
 * IMPORTANT:
 *
 * UserContext is no longer an independent authentication source.
 *
 * Authentication authority:
 *
 *   AuthProvider
 *      ↓
 *   GET /auth/me
 *      ↓
 *   server-validated user
 *
 * UserContext exists only for compatibility with older components.
 * ────────────────────────────────────────────────────────────── */

export const UserContext = createContext({
  user: EMPTY_USER,

  /*
   * Legacy compatibility only.
   *
   * Components must not mutate authentication/authorization state
   * through UserContext.
   */
  setUser: () => {},

  refreshUser: async () => ({
    ok: false,
  }),
});

/* ──────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────── */

function normalizeArray(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) =>
        item.trim()
      )
      .filter(Boolean);
  }

  return [];
}

function normalizeRole(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeRoles(
  primaryRole,
  roles
) {
  return Array.from(
    new Set([
      ...(primaryRole
        ? [
            normalizeRole(
              primaryRole
            ),
          ]
        : []),

      ...normalizeArray(roles)
        .map(normalizeRole)
        .filter(Boolean),
    ])
  ).filter(Boolean);
}

function normalizePermissions(
  permissions
) {
  return Array.from(
    new Set(
      normalizeArray(
        permissions
      )
        .map((permission) =>
          String(permission || "")
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    )
  );
}

function normalizePositiveIds(
  values
) {
  return Array.from(
    new Set(
      normalizeArray(values)
        .map(Number)
        .filter(
          (value) =>
            Number.isInteger(value) &&
            value > 0
        )
    )
  );
}

/* ──────────────────────────────────────────────────────────────
 * Provider
 * ────────────────────────────────────────────────────────────── */

export function UserProvider({
  children,
}) {
  const auth = useAuth();

  /*
   * AuthProvider is the only source.
   *
   * No local useState.
   * No mock developer.
   * No localStorage.
   * No JWT decoding.
   */
  const authUser =
    auth?.user || null;

  const normalizedUser =
    useMemo(() => {
      if (
        !authUser ||
        !auth?.authenticated
      ) {
        return EMPTY_USER;
      }

      const role =
        normalizeRole(
          authUser.role
        ) || null;

      const roles =
        normalizeRoles(
          role,
          authUser.roles
        );

      const permissions =
        normalizePermissions(
          authUser.permissions
        );

      /*
       * Support both naming conventions during frontend migration.
       *
       * allowedCostCentres is the current backend field.
       * cost_centres remains a compatibility alias for older components.
       */
      const allowedCostCentres =
        normalizePositiveIds(
          authUser.allowedCostCentres ??
          authUser.cost_centres
        );

      const modules =
        normalizeArray(
          authUser.modules
        )
          .map((moduleName) =>
            String(
              moduleName ||
              ""
            ).trim()
          )
          .filter(Boolean);

      /*
       * is_developer is derived from the SERVER-VALIDATED role only.
       *
       * It is no longer an independent override.
       */
      const isDeveloper =
        roles.includes(
          "developer"
        );

      return {
        ...EMPTY_USER,
        ...authUser,

        role,
        roles,
        permissions,
        modules,

        allowedCostCentres,

        /*
         * Legacy alias.
         */
        cost_centres:
          allowedCostCentres,

        is_developer:
          isDeveloper,

        is_platform_admin:
          authUser.is_platform_admin ===
          true,

        isAuthenticated:
          true,
      };
    }, [
      authUser,
      auth?.authenticated,
    ]);

  /* ────────────────────────────────────────────────────────────
   * Legacy setUser compatibility
   *
   * Previously components could write:
   *
   *   setUser({
   *     role: "developer"
   *   })
   *
   * That must no longer alter authorization state.
   *
   * Keep the function so older imports don't crash, but deliberately
   * ignore mutations.
   * ──────────────────────────────────────────────────────────── */

  const setUser =
    useCallback(
      (_nextUser) => {
        if (
          import.meta.env.DEV
        ) {
          console.warn(
            "[UserContext] setUser() is deprecated and ignored. " +
              "Authentication state is controlled by AuthProvider and /auth/me."
          );
        }
      },
      []
    );

  /* ────────────────────────────────────────────────────────────
   * Explicit server refresh
   * ──────────────────────────────────────────────────────────── */

  const refreshUser =
    useCallback(
      async () => {
        if (
          typeof auth?.refreshSession !==
          "function"
        ) {
          return {
            ok: false,
          };
        }

        return auth.refreshSession();
      },
      [auth]
    );

  const value =
    useMemo(
      () => ({
        user:
          normalizedUser,

        setUser,

        refreshUser,
      }),
      [
        normalizedUser,
        setUser,
        refreshUser,
      ]
    );

  return (
    <UserContext.Provider
      value={value}
    >
      {children}
    </UserContext.Provider>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Hook
 * ────────────────────────────────────────────────────────────── */

export function useUser() {
  return useContext(
    UserContext
  );
}